import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { campaignsAPI } from '../../services/api';
import {
  Upload, FileText, Phone, Play, Pause, Square, Plus, Trash2,
  Download, ChevronRight, Clock, Users, CheckCircle, XCircle,
  PhoneOff, PhoneMissed, Loader2, AlertCircle, Search, Filter,
  Calendar, Repeat, RefreshCw, Wifi, WifiOff
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CampaignsPage = () => {
  const navigate = useNavigate();
  const { clientUser } = useAuth();
  const { t } = useLanguage();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const fallbackPollRef = useRef(null);

  const fetchCampaigns = useCallback(async () => {
    if (!clientUser?.id) return;
    try {
      const response = await campaignsAPI.getAll(clientUser?.id);
      if (response.data.success) {
        setCampaigns(response.data.campaigns);
      }
    } catch (err) {
      console.error('Error fetching campaigns:', err);
    } finally {
      setLoading(false);
    }
  }, [clientUser?.id]);

  // WebSocket connection lifecycle
  useEffect(() => {
    if (!clientUser?.id) return undefined;

    let isMounted = true;

    const startFallbackPolling = () => {
      if (fallbackPollRef.current) return;
      fetchCampaigns();
      fallbackPollRef.current = setInterval(fetchCampaigns, 15000);
    };
    const stopFallbackPolling = () => {
      if (fallbackPollRef.current) {
        clearInterval(fallbackPollRef.current);
        fallbackPollRef.current = null;
      }
    };

    const connect = () => {
      if (!isMounted || !API) return;
      try {
        const wsProtocol = API.startsWith('https') ? 'wss' : 'ws';
        const wsUrl = `${API.replace(/^https?/, wsProtocol)}/api/ws/campaigns?client_id=${encodeURIComponent(clientUser.id)}`;
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          if (!isMounted) return;
          setWsConnected(true);
          stopFallbackPolling();
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'initial_state' || data.type === 'campaigns_update') {
              setCampaigns(data.campaigns || []);
              setLoading(false);
            }
          } catch (err) {
            console.error('Error parsing campaigns WS message:', err);
          }
        };

        ws.onclose = () => {
          if (!isMounted) return;
          setWsConnected(false);
          startFallbackPolling();
          // Try to reconnect after 5s
          if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = setTimeout(connect, 5000);
        };

        ws.onerror = () => {
          if (!isMounted) return;
          setWsConnected(false);
        };
      } catch (err) {
        console.error('Error connecting campaigns WS:', err);
        startFallbackPolling();
      }
    };

    connect();

    return () => {
      isMounted = false;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      stopFallbackPolling();
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (e) { /* noop */ }
        wsRef.current = null;
      }
    };
  }, [clientUser?.id, fetchCampaigns]);

  // Ask the server for a fresh snapshot (used after control actions for instant feedback)
  const requestRefresh = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'refresh' }));
    } else {
      fetchCampaigns();
    }
  }, [fetchCampaigns]);

  const handleControl = async (campaignId, action) => {
    try {
      await campaignsAPI.control(clientUser?.id, campaignId, action);
      requestRefresh();
    } catch (err) {
      console.error(`Error ${action} campaign:`, err);
    }
  };

  const handleDelete = async (campaignId) => {
    if (!window.confirm(t('campaigns.confirmDelete'))) return;
    try {
      await campaignsAPI.delete(clientUser?.id, campaignId);
      requestRefresh();
    } catch (err) {
      console.error('Error deleting campaign:', err);
    }
  };

  const filteredCampaigns = campaigns.filter(c => {
    if (filter === 'all') return true;
    return c.status === filter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'paused': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'stopped': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'completed': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'cancelled': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'scheduled': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getProgress = (campaign) => {
    const total = campaign.total_contacts || 1;
    const completed = (campaign.stats?.completed || 0) + 
                     (campaign.stats?.answered || 0) + 
                     (campaign.stats?.no_answer || 0) + 
                     (campaign.stats?.busy || 0) + 
                     (campaign.stats?.failed || 0);
    // Cap progress at 100%
    return Math.min(100, Math.round((completed / total) * 100));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">{t('campaigns.title')}</h1>
          <p className="text-gray-400">{t('campaigns.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
              wsConnected
                ? 'bg-green-500/10 text-green-400 border-green-500/30'
                : 'bg-gray-700/40 text-gray-400 border-gray-600'
            }`}
            data-testid="campaigns-ws-indicator"
            title={wsConnected ? 'Real-time updates active' : 'Reconnecting…'}
          >
            {wsConnected ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
            {wsConnected ? 'Live' : 'Offline'}
          </span>
          <button
            onClick={() => navigate('/client/campaigns/new')}
            className="flex items-center gap-2 px-4 py-2.5 bg-teal-500 hover:bg-teal-600 rounded-lg text-black font-medium transition-colors"
            data-testid="create-campaign-btn"
          >
            <Plus className="w-5 h-5" />
            {t('campaigns.newCampaign')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'draft', 'scheduled', 'running', 'paused', 'stopped', 'completed', 'cancelled'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filter === status
                ? 'bg-teal-400/20 text-teal-400 border border-teal-400/30'
                : 'bg-[#1a1a1f] text-gray-400 hover:text-white border border-gray-700'
            }`}
          >
            {t(`campaigns.filter${status.charAt(0).toUpperCase() + status.slice(1)}`)}
          </button>
        ))}
      </div>

      {/* Campaigns List */}
      {filteredCampaigns.length === 0 ? (
        <div className="text-center py-12 bg-[#0d1117] border border-gray-800 rounded-xl">
          <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">{t('campaigns.noCampaigns')}</h3>
          <p className="text-gray-400 mb-4">{t('campaigns.createFirstCampaign')}</p>
          <button
            onClick={() => navigate('/client/campaigns/new')}
            className="px-4 py-2 bg-teal-500 hover:bg-teal-600 rounded-lg text-black font-medium"
          >
            {t('campaigns.createCampaign')}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredCampaigns.map((campaign) => (
            <div
              key={campaign.id}
              className="bg-[#0d1117] border border-gray-800 rounded-xl p-5 hover:border-gray-700 transition-colors"
              data-testid={`campaign-card-${campaign.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-white">{campaign.name}</h3>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getStatusColor(campaign.status)}`}>
                      {campaign.status}
                    </span>
                  </div>
                  <p className="text-gray-400 text-sm mb-3">{campaign.description || t('campaigns.noDescription')}</p>
                  
                  <div className="flex items-center gap-6 text-sm">
                    <div className="flex items-center gap-1.5 text-gray-400">
                      <Users className="w-4 h-4" />
                      <span>{campaign.total_contacts} {t('campaigns.contacts')}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-gray-400">
                      <Phone className="w-4 h-4" />
                      <span>{t('campaigns.agent')}: {campaign.agent_name}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-gray-400">
                      <Clock className="w-4 h-4" />
                      <span>{campaign.delay_between_calls}s {t('campaigns.delay')}</span>
                    </div>
                    {campaign.scheduled_at && campaign.status === 'scheduled' && !campaign.recurrence_type && (
                      <div className="flex items-center gap-1.5 text-purple-400">
                        <Calendar className="w-4 h-4" />
                        <span>{t('campaigns.scheduled')}: {new Date(campaign.scheduled_at).toLocaleString()}</span>
                      </div>
                    )}
                    {campaign.recurrence_type && (
                      <div className="flex items-center gap-1.5 text-purple-400">
                        <Repeat className="w-4 h-4" />
                        <span>
                          {campaign.recurrence_type === 'daily' && t('campaigns.daily')}
                          {campaign.recurrence_type === 'weekly' && t('campaigns.weekly')}
                          {campaign.recurrence_type === 'monthly' && t('campaigns.monthly')}
                          {' at '}{campaign.recurrence_time} UTC
                          {campaign.run_count > 0 && ` (${campaign.run_count} ${t('campaigns.runs')})`}
                        </span>
                      </div>
                    )}
                    {campaign.next_run_at && campaign.status === 'scheduled' && campaign.recurrence_type && (
                      <div className="flex items-center gap-1.5 text-teal-400">
                        <RefreshCw className="w-4 h-4" />
                        <span>{t('campaigns.nextRun')}: {new Date(campaign.next_run_at).toLocaleString()}</span>
                      </div>
                    )}
                  </div>

                  {/* Progress Bar */}
                  {campaign.status !== 'draft' && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-gray-400">{t('campaigns.progress')}</span>
                        <span className="text-white font-medium">{getProgress(campaign)}%</span>
                      </div>
                      <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-teal-400 rounded-full transition-all duration-500"
                          style={{ width: `${getProgress(campaign)}%` }}
                        />
                      </div>
                      
                      {/* Stats */}
                      <div className="flex items-center gap-4 mt-2 text-xs">
                        <span className="flex items-center gap-1 text-green-400">
                          <CheckCircle className="w-3 h-3" />
                          {campaign.stats?.answered || 0} answered
                        </span>
                        <span className="flex items-center gap-1 text-yellow-400">
                          <PhoneMissed className="w-3 h-3" />
                          {campaign.stats?.no_answer || 0} no answer
                        </span>
                        <span className="flex items-center gap-1 text-red-400">
                          <XCircle className="w-3 h-3" />
                          {campaign.stats?.failed || 0} failed
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 ml-4">
                  {campaign.status === 'draft' && (
                    <button
                      onClick={() => handleControl(campaign.id, 'start')}
                      className="p-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-colors"
                      title="Start Campaign"
                      data-testid={`start-campaign-${campaign.id}`}
                    >
                      <Play className="w-5 h-5" />
                    </button>
                  )}
                  {campaign.status === 'running' && (
                    <button
                      onClick={() => handleControl(campaign.id, 'pause')}
                      className="p-2 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 rounded-lg transition-colors"
                      title="Pause Campaign"
                      data-testid={`pause-campaign-${campaign.id}`}
                    >
                      <Pause className="w-5 h-5" />
                    </button>
                  )}
                  {(campaign.status === 'paused' || campaign.status === 'stopped' || campaign.status === 'cancelled') && (
                    <button
                      onClick={() => handleControl(campaign.id, 'start')}
                      className="p-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-colors"
                      title="Resume Campaign"
                      data-testid={`resume-campaign-${campaign.id}`}
                    >
                      <Play className="w-5 h-5" />
                    </button>
                  )}
                  {(campaign.status === 'running' || campaign.status === 'paused') && (
                    <button
                      onClick={() => handleControl(campaign.id, 'stop')}
                      className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                      title="Stop Campaign"
                      data-testid={`stop-campaign-${campaign.id}`}
                    >
                      <Square className="w-5 h-5" />
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/client/campaigns/${campaign.id}`)}
                    className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                    title="View Details"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(campaign.id)}
                    className="p-2 bg-gray-700 hover:bg-red-500/30 text-gray-400 hover:text-red-400 rounded-lg transition-colors"
                    title="Delete Campaign"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CampaignsPage;
